import { METHOD_IDS } from '@/components/admin/reglages/method-contracts';
import { MethodFunctionCard } from '@/components/admin/reglages/method-function-card';
import { MethodStyleCard } from '@/components/admin/reglages/method-style-card';
import {
  type MethodDraft,
  methodProblems,
  withFunctionField,
  withStyleField,
} from '@/lib/method-draft';
import {
  METHOD_FUNCTIONS_TITLE,
  METHOD_INTRO,
  METHOD_PASS_ONE,
  METHOD_PASS_TWO,
  METHOD_STYLES_TITLE,
  blockedSentence,
} from '@/lib/method-labels';
import { ANALYST_FUNCTIONS, type MethodContent, NOYAUX } from '@matter_extractor/shared';
import { Alert, Badge } from '@pms-digital/design-system';
import { useId } from 'react';

/**
 * The three functions and the six styles of Réglages > Méthode, in the order of
 * the planche (analyste.md §4.4, maquette `Methode`): the sentence that says what
 * each pass reads, the functions in three cards, the styles in six, and - while
 * the development version is the seed's - the line that says so.
 *
 * It is the `renderFields` of the layer's editor and nothing more - the head, the
 * history, the autosave and the boxes are `VersionedEditor`'s - so it takes the
 * draft and hands back the next one, one field at a time.
 *
 * ## What blocks a version is said twice
 *
 * « Enregistrer une nouvelle version » exige tout non vide sauf les mots typiques
 * (§4.2), and the button of the head only knows « rien n'a changé ». So the field
 * that is empty says so under itself - and so would one past its bound, which the
 * fields cap at the typing and so rarely meet - and the summary above the cards
 * names the first three: the field to mend is rarely the one in view.
 *
 * ## Reading
 *
 * Under « Voir » and on a vitrine nothing is typable, nothing is « modifié » and
 * nothing is refused: a version a server keeps is whole.
 */

export interface MethodFieldsProps {
  readonly draft: MethodDraft;
  /** The development version the draft started from: what « modifié » is measured against. */
  readonly stored: MethodContent;
  /** A version opened by « Voir », or a vitrine: nothing is typable. */
  readonly readOnly: boolean;
  /** « v1 : graine de #196, ... » while the development version is the seed's, else null. */
  readonly seedNote: string | null;
  readonly onDraft: (next: MethodDraft) => void;
}

/** The pill beside a section title: which pass reads it - the package `Badge`, accent tone. */
function PassBadge({ label }: { readonly label: string }) {
  return <Badge tone="accent">{label}</Badge>;
}

const SECTION_TITLE = 'font-semibold text-[22px] text-gray-900 leading-[30px]';
const GRID = 'grid items-start gap-6 md:grid-cols-2 xl:grid-cols-3';

export function MethodFields({ draft, stored, readOnly, seedNote, onDraft }: MethodFieldsProps) {
  const functionsTitleId = useId();
  const stylesTitleId = useId();
  const problems = readOnly ? [] : methodProblems(draft);

  return (
    <div className="space-y-5">
      <p
        data-testid={METHOD_IDS.intro}
        className="max-w-[96ch] text-gray-700 text-small leading-[21px]"
      >
        {METHOD_INTRO}
      </p>

      {problems.length === 0 ? null : (
        <div data-testid={METHOD_IDS.blocked}>
          <Alert tone="warning">{blockedSentence(problems)}</Alert>
        </div>
      )}

      <section
        aria-labelledby={functionsTitleId}
        data-testid={METHOD_IDS.functions}
        className="mt-2 space-y-3.5"
      >
        <div className="flex items-center gap-2.5">
          <h2 id={functionsTitleId} className={SECTION_TITLE}>
            {METHOD_FUNCTIONS_TITLE}
          </h2>
          <PassBadge label={METHOD_PASS_ONE} />
        </div>
        <div className={GRID}>
          {ANALYST_FUNCTIONS.map((fn) => (
            <MethodFunctionCard
              key={fn}
              fn={fn}
              draft={draft}
              stored={stored}
              readOnly={readOnly}
              onField={(field, text) => onDraft(withFunctionField(draft, fn, field, text))}
            />
          ))}
        </div>
      </section>

      <section
        aria-labelledby={stylesTitleId}
        data-testid={METHOD_IDS.styles}
        className="mt-2 space-y-3.5"
      >
        <div className="flex items-center gap-2.5">
          <h2 id={stylesTitleId} className={SECTION_TITLE}>
            {METHOD_STYLES_TITLE}
          </h2>
          <PassBadge label={METHOD_PASS_TWO} />
        </div>
        <div className={GRID}>
          {NOYAUX.map((noyau) => (
            <MethodStyleCard
              key={noyau.code}
              noyau={noyau}
              draft={draft}
              stored={stored}
              readOnly={readOnly}
              onField={(field, text) => onDraft(withStyleField(draft, noyau.code, field, text))}
            />
          ))}
        </div>
      </section>

      {seedNote === null ? null : (
        <p data-testid={METHOD_IDS.seed} className="text-caption text-gray-600 leading-4">
          {seedNote}
        </p>
      )}
    </div>
  );
}
